/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import listItems from 'flarum/common/helpers/listItems';
import Dropdown from 'flarum/common/components/Dropdown';
import ItemList from 'flarum/common/utils/ItemList';
import type Mithril from 'mithril';
import type Article from 'flarum/orell-articles/models/Article';
import ReviewList from './ReviewList';
import CreateReviewPrimaryButton from './CreateReviewPrimaryButton';
import { slug } from '../../common';
import ReviewListState from '../states/ReviewListState';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import RatingsCard from './RatingsCard';

export interface ArticlePageReviewsAttrs extends ComponentAttrs {
	article: Article;
}

/** The URL parameters this list reads. */
type ArticleReviewsParams = {
	sort?: string;
	q?: string;
	filter?: string;
};

export default class ArticlePageReviews extends Component<ArticlePageReviewsAttrs, ReviewListState> {
	canStartReview!: boolean;

	oninit(vnode: Mithril.Vnode<ArticlePageReviewsAttrs, this>) {
		super.oninit(vnode);

		const params = this.params();
		const { article } = this.attrs;

		if (app.session.user) {
			this.canStartReview = app.session.user.canCreateReviews();
		} else {
			this.canStartReview = app.forum.attribute<boolean>('canStartDiscussion') || !app.session.user;
		}

		this.state = new ReviewListState(params, 1, article);

		app.articleReviews = this.state;

		this.state.refresh();
	}

	view() {
		const { article } = this.attrs;
		const allReviewsCount = article.allReviewsCount();

		const ownReview = article.ownReview();

		let canMakeSimpleRating = false;
		if (app.session.user?.canCreateReviews() ?? false) {
			if (!ownReview || ownReview.isSimple()) {
				canMakeSimpleRating = true;
			}
		}

		return (
			<div className="ArticlePage-reviews">
				<hr className="ArticlePage-reviews-divider" />
				<div className="ArticlePage-reviews--header">
					<RatingsCard article={article} />
				</div>

				{!article.reviewCount() ? (
					<div className="ArticlePageNoReviews">
						{!allReviewsCount && <h2>{app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.be_first')}</h2>}
						<img src={`/assets/extensions/${slug}/new_review_icon.svg`} />
						<CreateReviewPrimaryButton article={article} />
					</div>
				) : (
					<div>
						{(!ownReview || canMakeSimpleRating) && <CreateReviewPrimaryButton article={article} reviewsState={this.state} />}

						<div className="IndexPage-toolbar">
							<ul className="IndexPage-toolbar-view">{listItems(this.viewItems().toArray())}</ul>
							<ul className="IndexPage-toolbar-action">{listItems(this.actionItems().toArray())}</ul>
						</div>
						<ReviewList state={this.state} noCover={true} />
					</div>
				)}
			</div>
		);
	}

	viewItems() {
		const items = new ItemList<Mithril.Children>();
		const sortMap = this.state.sortMap();

		const sortOptions: Record<string, Mithril.Children> = {};
		for (const i in sortMap) {
			sortOptions[i] = app.translator.trans('core.forum.index_sort.' + i + '_button');
		}

		items.add(
			'sort',
			<Dropdown
				buttonClassName="Button"
				label={sortOptions[this.params().sort!] || Object.keys(sortMap).map((key) => sortOptions[key])[0]}
				accessibleToggleLabel={app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label') as string}
			>
				{Object.keys(sortOptions).map((value) => {
					const label = sortOptions[value];
					const active = (this.params().sort || Object.keys(sortMap)[0]) === value;

					return (
						<Button icon={active ? 'far fa-check' : true} onclick={this.changeSort.bind(this, value)} active={active}>
							{label}
						</Button>
					);
				})}
			</Dropdown>
		);

		return items;
	}

	actionItems() {
		const items = new ItemList<Mithril.Children>();

		items.add(
			'refresh',
			<Button
				title={app.translator.trans('core.forum.index.refresh_tooltip')}
				icon="far fa-sync"
				className="Button Button--icon"
				onclick={() => this.state.refresh()}
			/>
		);

		return items;
	}

	changeSort(sort: string) {
		const params = this.params();
		const { article } = this.attrs;

		if (sort === Object.keys(this.state.sortMap())[0]) {
			delete params.sort;
		} else {
			params.sort = sort;
		}

		setRouteWithForcedRefresh(app.route.of_article(article, params));
	}

	/**
	 * The parameters that survive a filter change.
	 */
	stickyParams(): ArticleReviewsParams {
		return {
			sort: m.route.param('sort'),
			q: m.route.param('q'),
		};
	}

	params(): ArticleReviewsParams {
		const params = this.stickyParams();

		params.filter = m.route.param('filter');

		return params;
	}
}
